import React, { useMemo, useState, useEffect, useContext, useRef } from 'react';
import { AuthContext } from '../../contexts/AuthContext';
import { saveWellnessLog, getWellnessLogs, updateList } from '../../utils/firebase';
import {
  Box,
  Typography,
  IconButton,
  Divider,
  Chip,
  TextField,
  Tooltip,
  Fade,
  Grow,
  Button,
  Popover,
  Menu
} from '@mui/material';
import { parseISO, isValid, format } from 'date-fns';
import AssignmentLateIcon from '@mui/icons-material/AssignmentLate';
import WatchLaterIcon from '@mui/icons-material/WatchLater';
import FitnessCenterIcon from '@mui/icons-material/FitnessCenter';
import AppleIcon from '@mui/icons-material/Apple';
import BedIcon from '@mui/icons-material/Bed';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import RadioButtonUncheckedIcon from '@mui/icons-material/RadioButtonUnchecked';
import LocalFireDepartmentIcon from '@mui/icons-material/LocalFireDepartment';
import SentimentVerySatisfiedIcon from '@mui/icons-material/SentimentVerySatisfied';
import SentimentSatisfiedIcon from '@mui/icons-material/SentimentSatisfied';
import SentimentNeutralIcon from '@mui/icons-material/SentimentNeutral';
import SentimentDissatisfiedIcon from '@mui/icons-material/SentimentDissatisfied';
import SentimentVeryDissatisfiedIcon from '@mui/icons-material/SentimentVeryDissatisfied';
import WallpaperIcon from '@mui/icons-material/Wallpaper';
import CakeIcon from '@mui/icons-material/Cake';
import EditIcon from '@mui/icons-material/Edit';
import SaveIcon from '@mui/icons-material/Save';

const Dashboard = ({ lists, onSelectList, showPastDays = false, backgroundImage, onBackgroundClick }) => {
  const { user } = useContext(AuthContext);
  const [highlight, setHighlight] = useState('');
  const [exercised, setExercised] = useState(false);
  const [eatenFruits, setEatenFruits] = useState(false);
  const [sleptWell, setSleptWell] = useState(false);
  const [mood, setMood] = useState('');
  const [dailyLogs, setDailyLogs] = useState([]);
  const [mounted, setMounted] = useState(false);
  const [moodAnchorEl, setMoodAnchorEl] = useState(null);
  const isTypingRef = useRef(false);
  const typingTimeoutRef = useRef(null);
  const [showAllOverdue, setShowAllOverdue] = useState(false);
  const [showAllUpcoming, setShowAllUpcoming] = useState(false);
  const [editingDate, setEditingDate] = useState(null);
  const [editData, setEditData] = useState({});
  const [editMoodAnchorEl, setEditMoodAnchorEl] = useState(null);

  useEffect(() => {
    setMounted(true);

    // Cleanup timeout on unmount
    return () => {
      if (typingTimeoutRef.current) {
        clearTimeout(typingTimeoutRef.current);
      }
    };
  }, []);

  // Load daily logs from localStorage initially, then try Firebase
  useEffect(() => {
    // Load from localStorage first
    const savedLogs = localStorage.getItem('dailyWellnessLogs');
    if (savedLogs) {
      const logs = JSON.parse(savedLogs);
      setDailyLogs(logs);

      const today = format(new Date(), 'yyyy-MM-dd');
      const todayLog = logs.find(log => log.date === today);
      if (todayLog) {
        setHighlight(todayLog.highlight || '');
        setExercised(todayLog.exercised || false);
        setEatenFruits(todayLog.eatenFruits || false);
        setSleptWell(todayLog.sleptWell || false);
        setMood(todayLog.mood || '');
      }
    }

    // Try to sync with Firebase
    if (!user) return;

    const unsubscribe = getWellnessLogs(user.uid, (logs) => {
      if (logs.length > 0) {
        setDailyLogs(logs);
        localStorage.setItem('dailyWellnessLogs', JSON.stringify(logs));

        const today = format(new Date(), 'yyyy-MM-dd');
        const todayLog = logs.find(log => log.date === today);
        if (todayLog) {
          // Only update highlight if user is not actively typing
          if (!isTypingRef.current) {
            setHighlight(todayLog.highlight || '');
          }
          setExercised(todayLog.exercised || false);
          setEatenFruits(todayLog.eatenFruits || false);
          setSleptWell(todayLog.sleptWell || false);
          setMood(todayLog.mood || '');
        }
      }
    });

    return () => unsubscribe();
  }, [user]);

  // Save today's log to both localStorage and Firebase
  useEffect(() => {
    // Skip initial render
    if (!mounted) return;

    const today = format(new Date(), 'yyyy-MM-dd');
    const todayLog = {
      date: today,
      highlight,
      exercised,
      eatenFruits,
      sleptWell,
      mood,
      timestamp: new Date().toISOString()
    };

    // Save to localStorage
    const savedLogs = localStorage.getItem('dailyWellnessLogs');
    const existingLogs = savedLogs ? JSON.parse(savedLogs) : [];
    const updatedLogs = existingLogs.filter(log => log.date !== today);
    updatedLogs.unshift(todayLog);
    const trimmedLogs = updatedLogs.slice(0, 30);
    localStorage.setItem('dailyWellnessLogs', JSON.stringify(trimmedLogs));

    console.log('Saved to localStorage:', todayLog);

    // Try to save to Firebase
    if (user) {
      saveWellnessLog(user.uid, today, todayLog).then(() => {
        console.log('Saved to Firebase:', todayLog);
      }).catch(err => {
        console.log('Firebase save failed, using localStorage only:', err.message);
      });
    }
  }, [user, highlight, exercised, eatenFruits, sleptWell, mood, mounted]);

  // Handle editing past days
  const handleEditPastDay = (log) => {
    setEditingDate(log.date);
    setEditData({
      highlight: log.highlight || '',
      exercised: log.exercised || false,
      eatenFruits: log.eatenFruits || false,
      sleptWell: log.sleptWell || false,
      mood: log.mood || ''
    });
  };

  const handleSavePastDay = async () => {
    if (!editingDate) return;

    const updatedLog = {
      date: editingDate,
      ...editData,
      timestamp: new Date().toISOString()
    };

    // Optimistically update in state
    const updatedLogs = dailyLogs.map(log =>
      log.date === editingDate ? updatedLog : log
    );
    setDailyLogs(updatedLogs);

    // Save to localStorage
    localStorage.setItem('dailyWellnessLogs', JSON.stringify(updatedLogs));

    // Save to Firebase - this will trigger the listener to update with confirmed data
    if (user) {
      try {
        await saveWellnessLog(user.uid, editingDate, updatedLog);
      } catch (err) {
        console.error('Failed to save to Firebase:', err);
      }
    }

    // Clear editing state after save completes
    setEditingDate(null);
    setEditData({});
  };

  // Calculate streak
  const streak = useMemo(() => {
    let count = 0;
    const sortedLogs = [...dailyLogs].sort((a, b) => b.date.localeCompare(a.date));

    for (let i = 0; i < sortedLogs.length; i++) {
      const log = sortedLogs[i];
      const expectedDate = format(
        new Date(Date.now() - i * 24 * 60 * 60 * 1000),
        'yyyy-MM-dd'
      );

      if (log.date === expectedDate && (log.highlight || log.exercised || log.eatenFruits || log.sleptWell)) {
        count++;
      } else {
        break;
      }
    }

    return count;
  }, [dailyLogs]);

  // Get all incomplete todos
  const incompleteTodos = useMemo(() => {
    const todos = [];
    lists.forEach(list => {
      if (list.type === 'todo' && list.items) {
        list.items
          .filter(item => !item.completed)
          .forEach(item => {
            todos.push({
              ...item,
              listId: list.id,
              listName: list.name
            });
          });
      }
    });
    return todos;
  }, [lists]);

  // Overdue todos
  const overdueTodos = useMemo(() => {
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    return incompleteTodos
      .filter(item => {
        if (!item.dueDate) return false;
        const dueDate = parseISO(item.dueDate);
        return isValid(dueDate) && dueDate < today;
      })
      .sort((a, b) => parseISO(a.dueDate) - parseISO(b.dueDate));
  }, [incompleteTodos]);

  // Upcoming todos
  const upcomingTodos = useMemo(() => {
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const sevenDaysLater = new Date(today.getTime() + 7 * 24 * 60 * 60 * 1000);

    return incompleteTodos
      .filter(item => {
        if (!item.dueDate) return false;
        const dueDate = parseISO(item.dueDate);
        return isValid(dueDate) && dueDate >= today && dueDate <= sevenDaysLater;
      })
      .sort((a, b) => parseISO(a.dueDate) - parseISO(b.dueDate));
  }, [incompleteTodos]);

  // Upcoming birthdays (next 7 days)
  const upcomingBirthdays = useMemo(() => {
    const birthdays = [];
    lists.forEach(list => {
      if (list.type === 'birthday' && list.items) {
        list.items.forEach(item => {
          if (item.birthDate) {
            const birth = new Date(item.birthDate);
            const today = new Date();
            today.setHours(0, 0, 0, 0);
            const thisYear = today.getFullYear();

            let nextBirthday = new Date(thisYear, birth.getMonth(), birth.getDate());
            nextBirthday.setHours(0, 0, 0, 0);

            if (nextBirthday < today) {
              nextBirthday = new Date(thisYear + 1, birth.getMonth(), birth.getDate());
              nextBirthday.setHours(0, 0, 0, 0);
            }

            const daysUntil = Math.floor((nextBirthday - today) / (1000 * 60 * 60 * 24));

            if (daysUntil >= 0 && daysUntil <= 7) {
              birthdays.push({
                ...item,
                listId: list.id,
                listName: list.name,
                nextBirthday: nextBirthday.toISOString(),
                daysUntil
              });
            }
          }
        });
      }
    });
    return birthdays.sort((a, b) => a.daysUntil - b.daysUntil);
  }, [lists]);

  const formatDate = (dateString) => {
    try {
      return format(parseISO(dateString), 'MMM d');
    } catch {
      return dateString;
    }
  };

  const formatLogDate = (dateString) => {
    try {
      const date = parseISO(dateString);
      const today = new Date();
      const diffTime = today - date;
      const diffDays = Math.floor(diffTime / (1000 * 60 * 60 * 24));

      if (diffDays === 0) return 'Today';
      if (diffDays === 1) return 'Yesterday';
      return format(date, 'EEEE, MMM d');
    } catch {
      return dateString;
    }
  };

  const handleToggleTodoComplete = async (todoItem, e) => {
    e.stopPropagation(); // Prevent navigation to list

    // Find the list containing this todo
    const list = lists.find(l => l.id === todoItem.listId);
    if (!list) return;

    // Update the item to mark it as completed
    const updatedItems = list.items.map(item => {
      if (item.id === todoItem.id) {
        return {
          ...item,
          completed: true,
          completedAt: new Date().toISOString()
        };
      }
      return item;
    });

    // Save to Firebase
    await updateList(list.id, { items: updatedItems });
  };

  const WellnessIcon = ({ icon: Icon, checked, onClick, title, delay, color = '#4caf50' }) => (
    <Grow in={mounted} timeout={800} style={{ transformOrigin: '0 0 0' }} {...(delay ? { style: { transitionDelay: `${delay}ms` } } : {})}>
      <Tooltip title={title} arrow placement="top">
        <IconButton
          onClick={(e) => onClick(e)}
            sx={{
              position: 'relative',
              p: 0,
              width: 70,
              height: 70,
              borderRadius: '50%',
              border: '2px solid',
              borderColor: checked ? color : 'rgba(255, 255, 255, 0.1)',
              backgroundColor: checked ? `${color}1a` : 'transparent',
              transition: 'all 0.4s cubic-bezier(0.4, 0, 0.2, 1)',
              '&:hover': {
                borderColor: color,
                backgroundColor: `${color}26`,
                transform: 'scale(1.1) translateY(-4px)',
                boxShadow: checked ? `0 8px 24px ${color}4d` : `0 8px 24px ${color}33`
              }
            }}
          >
            <Icon
              sx={{
                fontSize: 32,
                color: checked ? color : 'rgba(255, 255, 255, 0.4)',
                transition: 'all 0.3s'
              }}
            />
            {checked && (
              <Fade in={checked}>
                <CheckCircleIcon
                  sx={{
                    fontSize: 20,
                    color: color,
                    position: 'absolute',
                    top: -4,
                    right: -4,
                    backgroundColor: 'background.paper',
                    borderRadius: '50%'
                  }}
                />
              </Fade>
            )}
          </IconButton>
        </Tooltip>
      </Grow>
  );

  // If showing past days, render only the past days view
  if (showPastDays) {
    return (
      <Box
        sx={{
          minHeight: 'calc(100vh - 64px)',
          width: '100%',
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          pt: { xs: 4, sm: 6 },
          pb: 10,
          px: { xs: 3, sm: 4, md: 6 }
        }}
      >
        <Box sx={{ width: '100%', maxWidth: '800px' }}>
          <Typography
            variant="h4"
            sx={{
              mb: 4,
              fontWeight: 300,
              color: 'rgba(255, 255, 255, 0.95)',
              textAlign: 'center'
            }}
          >
            Wellness Log
          </Typography>
          {dailyLogs.length > 1 ? (
            dailyLogs.slice(1, 31).map((log, index) => {
              const isEditing = editingDate === log.date;
              const currentData = isEditing ? editData : log;

              return (
                <Fade in={mounted} timeout={300 + index * 50} key={log.date}>
                  <Box
                    sx={{
                      py: 2,
                      px: 2.5,
                      mb: 1.5,
                      borderRadius: 2,
                      backgroundColor: isEditing ? 'rgba(255, 255, 255, 0.06)' : 'rgba(255, 255, 255, 0.02)',
                      border: '1px solid',
                      borderColor: isEditing ? 'primary.main' : 'rgba(255, 255, 255, 0.08)',
                      transition: 'all 0.3s',
                      '&:hover': {
                        backgroundColor: isEditing ? 'rgba(255, 255, 255, 0.06)' : 'rgba(255, 255, 255, 0.04)',
                        borderColor: isEditing ? 'primary.main' : 'rgba(255, 255, 255, 0.12)'
                      }
                    }}
                  >
                    {/* Header with date and icons */}
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: currentData.highlight ? 1.5 : 0 }}>
                      <Typography
                        variant="caption"
                        sx={{
                          fontWeight: 600,
                          color: 'rgba(255, 255, 255, 0.5)',
                          textTransform: 'uppercase',
                          letterSpacing: '0.05em',
                          fontSize: '0.7rem'
                        }}
                      >
                        {formatLogDate(log.date)}
                      </Typography>

                      <Box sx={{ display: 'flex', gap: 1.5, alignItems: 'center' }}>
                        {/* Wellness Icons */}
                        {isEditing ? (
                          <>
                            {/* Mood selector for editing */}
                            <Tooltip title="Select Mood" arrow>
                              <IconButton
                                size="small"
                                onClick={(e) => setEditMoodAnchorEl(e.currentTarget)}
                                sx={{ p: 0.5 }}
                              >
                                {editData.mood === 'great' ? (
                                  <SentimentVerySatisfiedIcon sx={{ fontSize: 18, color: '#4caf50' }} />
                                ) : editData.mood === 'good' ? (
                                  <SentimentSatisfiedIcon sx={{ fontSize: 18, color: '#8bc34a' }} />
                                ) : editData.mood === 'okay' ? (
                                  <SentimentNeutralIcon sx={{ fontSize: 18, color: '#ffc107' }} />
                                ) : editData.mood === 'bad' ? (
                                  <SentimentDissatisfiedIcon sx={{ fontSize: 18, color: '#ff9800' }} />
                                ) : editData.mood === 'terrible' ? (
                                  <SentimentVeryDissatisfiedIcon sx={{ fontSize: 18, color: '#f44336' }} />
                                ) : (
                                  <SentimentNeutralIcon sx={{ fontSize: 18, color: 'rgba(255, 255, 255, 0.15)' }} />
                                )}
                              </IconButton>
                            </Tooltip>
                            <Tooltip title="Exercise" arrow>
                              <IconButton
                                size="small"
                                onClick={() => setEditData({ ...editData, exercised: !editData.exercised })}
                                sx={{ p: 0.5 }}
                              >
                                <FitnessCenterIcon sx={{ fontSize: 18, color: editData.exercised ? 'success.main' : 'rgba(255, 255, 255, 0.15)' }} />
                              </IconButton>
                            </Tooltip>
                            <Tooltip title="Fruits & Vegetables" arrow>
                              <IconButton
                                size="small"
                                onClick={() => setEditData({ ...editData, eatenFruits: !editData.eatenFruits })}
                                sx={{ p: 0.5 }}
                              >
                                <AppleIcon sx={{ fontSize: 18, color: editData.eatenFruits ? 'success.main' : 'rgba(255, 255, 255, 0.15)' }} />
                              </IconButton>
                            </Tooltip>
                            <Tooltip title="Sleep" arrow>
                              <IconButton
                                size="small"
                                onClick={() => setEditData({ ...editData, sleptWell: !editData.sleptWell })}
                                sx={{ p: 0.5 }}
                              >
                                <BedIcon sx={{ fontSize: 18, color: editData.sleptWell ? 'success.main' : 'rgba(255, 255, 255, 0.15)' }} />
                              </IconButton>
                            </Tooltip>
                          </>
                        ) : (
                          <>
                            {currentData.mood && (
                              <Tooltip title={`Mood: ${currentData.mood}`} arrow>
                                {currentData.mood === 'great' ? <SentimentVerySatisfiedIcon sx={{ fontSize: 18, color: '#4caf50' }} /> :
                                currentData.mood === 'good' ? <SentimentSatisfiedIcon sx={{ fontSize: 18, color: '#8bc34a' }} /> :
                                currentData.mood === 'okay' ? <SentimentNeutralIcon sx={{ fontSize: 18, color: '#ffc107' }} /> :
                                currentData.mood === 'bad' ? <SentimentDissatisfiedIcon sx={{ fontSize: 18, color: '#ff9800' }} /> :
                                currentData.mood === 'terrible' ? <SentimentVeryDissatisfiedIcon sx={{ fontSize: 18, color: '#f44336' }} /> : null}
                              </Tooltip>
                            )}
                            <Tooltip title="Exercise" arrow>
                              <FitnessCenterIcon sx={{ fontSize: 18, color: currentData.exercised ? 'success.main' : 'rgba(255, 255, 255, 0.15)' }} />
                            </Tooltip>
                            <Tooltip title="Fruits & Vegetables" arrow>
                              <AppleIcon sx={{ fontSize: 18, color: currentData.eatenFruits ? 'success.main' : 'rgba(255, 255, 255, 0.15)' }} />
                            </Tooltip>
                            <Tooltip title="Sleep" arrow>
                              <BedIcon sx={{ fontSize: 18, color: currentData.sleptWell ? 'success.main' : 'rgba(255, 255, 255, 0.15)' }} />
                            </Tooltip>
                          </>
                        )}

                        {/* Edit/Save button */}
                        {isEditing ? (
                          <IconButton
                            size="small"
                            onClick={handleSavePastDay}
                            sx={{
                              p: 0.5,
                              backgroundColor: 'rgba(13, 71, 161, 0.2)',
                              border: '1px solid rgba(13, 71, 161, 0.4)',
                              '&:hover': {
                                backgroundColor: 'rgba(13, 71, 161, 0.3)',
                              }
                            }}
                          >
                            <SaveIcon sx={{ fontSize: 18, color: '#0d47a1' }} />
                          </IconButton>
                        ) : (
                          <IconButton size="small" onClick={() => handleEditPastDay(log)} sx={{ p: 0.5 }}>
                            <EditIcon sx={{ fontSize: 18, color: 'rgba(255, 255, 255, 0.4)' }} />
                          </IconButton>
                        )}
                      </Box>
                    </Box>

                    {/* Highlight text */}
                    {isEditing ? (
                      <TextField
                        fullWidth
                        value={editData.highlight}
                        onChange={(e) => setEditData({ ...editData, highlight: e.target.value })}
                        onKeyPress={(e) => {
                          if (e.key === 'Enter') {
                            handleSavePastDay();
                          }
                        }}
                        placeholder="What made this day special?"
                        variant="standard"
                        inputProps={{ maxLength: 200 }}
                        sx={{
                          '& .MuiInputBase-input': {
                            fontSize: '0.875rem',
                            color: 'rgba(255, 255, 255, 0.75)',
                            fontStyle: 'italic'
                          }
                        }}
                      />
                    ) : currentData.highlight ? (
                      <Typography
                        variant="body2"
                        sx={{
                          color: 'rgba(255, 255, 255, 0.75)',
                          fontStyle: 'italic',
                          pl: 2,
                          borderLeft: '2px solid rgba(0, 137, 123, 0.5)',
                          lineHeight: 1.4,
                          fontSize: '0.875rem'
                        }}
                      >
                        {currentData.highlight}
                      </Typography>
                    ) : null}
                  </Box>
                </Fade>
              );
            })
          ) : (
            <Box sx={{ textAlign: 'center', py: 8 }}>
              <Typography variant="body1" color="text.secondary">
                No past logs yet. Start tracking your wellness journey today!
              </Typography>
            </Box>
          )}
        </Box>

        {/* Edit Mood Selector Popover */}
        <Popover
          open={Boolean(editMoodAnchorEl)}
          anchorEl={editMoodAnchorEl}
          onClose={() => setEditMoodAnchorEl(null)}
          disableRestoreFocus
          disableEnforceFocus
          disablePortal={false}
          anchorOrigin={{
            vertical: 'bottom',
            horizontal: 'center',
          }}
          transformOrigin={{
            vertical: 'top',
            horizontal: 'center',
          }}
          slotProps={{
            root: {
              style: {
                zIndex: 1300
              }
            }
          }}
          PaperProps={{
            sx: {
              mt: 1,
              backgroundColor: 'rgba(30, 30, 30, 0.95)',
              backdropFilter: 'blur(10px)',
              borderRadius: 2,
              p: 1.5,
              boxShadow: '0 8px 32px rgba(0, 0, 0, 0.4)'
            }
          }}
        >
          <Box sx={{ display: 'flex', gap: 1 }}>
            {[
              { value: 'great', icon: SentimentVerySatisfiedIcon, label: 'Great', color: '#4caf50' },
              { value: 'good', icon: SentimentSatisfiedIcon, label: 'Good', color: '#8bc34a' },
              { value: 'okay', icon: SentimentNeutralIcon, label: 'Okay', color: '#ffc107' },
              { value: 'bad', icon: SentimentDissatisfiedIcon, label: 'Bad', color: '#ff9800' },
              { value: 'terrible', icon: SentimentVeryDissatisfiedIcon, label: 'Terrible', color: '#f44336' }
            ].map(({ value, icon: Icon, label, color }) => (
              <IconButton
                key={value}
                aria-label={label}
                title={label}
                onClick={() => {
                  setEditData({ ...editData, mood: value });
                  setEditMoodAnchorEl(null);
                }}
                sx={{
                  p: 0.5,
                  width: 40,
                  height: 40,
                  borderRadius: '50%',
                  border: '2px solid',
                  borderColor: editData.mood === value ? color : 'rgba(255, 255, 255, 0.1)',
                  backgroundColor: editData.mood === value ? `${color}20` : 'transparent',
                  transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                  '&:hover': {
                    borderColor: color,
                    backgroundColor: `${color}15`,
                    transform: 'scale(1.1)',
                  }
                }}
              >
                <Icon
                  sx={{
                    fontSize: 20,
                    color: editData.mood === value ? color : 'rgba(255, 255, 255, 0.6)',
                    transition: 'all 0.3s'
                  }}
                />
              </IconButton>
            ))}
          </Box>
        </Popover>
      </Box>
    );
  }

  return (
    <Box
      sx={{
        minHeight: 'calc(100vh - 64px)',
        width: '100%',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        pt: { xs: 4, sm: 6 },
        pb: 10,
        px: { xs: 3, sm: 4, md: 6 }
      }}
    >
      <Box sx={{ width: '100%', maxWidth: '680px', flexGrow: 1, display: 'flex', flexDirection: 'column' }}>
        {/* Highlight Input */}
        <Fade in={mounted} timeout={800}>
          <Box sx={{ mb: 6, textAlign: 'center' }}>
            <Typography
              variant="h3"
              sx={{
                mb: 4,
                fontWeight: 300,
                fontSize: { xs: '2rem', sm: '2.5rem' },
                color: 'rgba(255, 255, 255, 0.95)',
                letterSpacing: '-0.02em'
              }}
            >
              Highlight of the day
            </Typography>
            <TextField
              fullWidth
              value={highlight}
              onChange={(e) => {
                // Mark as typing to prevent Firebase from overwriting
                isTypingRef.current = true;
                setHighlight(e.target.value);

                // Clear existing timeout
                if (typingTimeoutRef.current) {
                  clearTimeout(typingTimeoutRef.current);
                }

                // Set flag to false after 500ms of no typing
                typingTimeoutRef.current = setTimeout(() => {
                  isTypingRef.current = false;
                }, 500);
              }}
              onKeyPress={(e) => {
                if (e.key === 'Enter') {
                  e.target.blur();
                  // Immediately stop typing mode on Enter
                  isTypingRef.current = false;
                  if (typingTimeoutRef.current) {
                    clearTimeout(typingTimeoutRef.current);
                  }
                }
              }}
              placeholder="What made today special?"
              variant="standard"
              inputProps={{
                maxLength: 200
              }}
              sx={{
                '& .MuiInputBase-input': {
                  textAlign: 'center',
                  fontSize: { xs: '1.1rem', sm: '1.25rem' },
                  fontWeight: 300,
                  color: 'rgba(255, 255, 255, 0.9)',
                  letterSpacing: '0.01em',
                  '&::placeholder': {
                    color: 'rgba(255, 255, 255, 0.3)',
                    opacity: 1
                  }
                },
                '& .MuiInput-underline:before': {
                  borderBottom: '1px solid rgba(255, 255, 255, 0.1)'
                },
                '& .MuiInput-underline:hover:not(.Mui-disabled):before': {
                  borderBottom: '2px solid rgba(255, 255, 255, 0.3)'
                },
                '& .MuiInput-underline:after': {
                  borderBottom: '2px solid rgba(0, 137, 123, 0.7)'
                }
              }}
            />
          </Box>
        </Fade>

        {/* Wellness Icons */}
        <Box sx={{ mb: 8, display: 'flex', justifyContent: 'center', gap: { xs: 3, sm: 4 }, px: { xs: 2, sm: 4 } }}>
          {/* Mood Selector Icon */}
          <Grow in={mounted} timeout={800} style={{ transformOrigin: '0 0 0', transitionDelay: '50ms' }}>
            <Tooltip title="How are you feeling?" arrow placement="top">
              <IconButton
                onClick={(e) => setMoodAnchorEl(e.currentTarget)}
                sx={{
                  position: 'relative',
                  p: 0,
                  width: 70,
                  height: 70,
                  borderRadius: '50%',
                  border: '2px solid',
                  borderColor: mood === 'great' ? '#4caf50' :
                               mood === 'good' ? '#8bc34a' :
                               mood === 'okay' ? '#ffc107' :
                               mood === 'bad' ? '#ff9800' :
                               mood === 'terrible' ? '#f44336' :
                               'rgba(255, 255, 255, 0.1)',
                  backgroundColor: mood === 'great' ? 'rgba(76, 175, 80, 0.1)' :
                                   mood === 'good' ? 'rgba(139, 195, 74, 0.1)' :
                                   mood === 'okay' ? 'rgba(255, 193, 7, 0.1)' :
                                   mood === 'bad' ? 'rgba(255, 152, 0, 0.1)' :
                                   mood === 'terrible' ? 'rgba(244, 67, 54, 0.1)' :
                                   'transparent',
                  transition: 'all 0.4s cubic-bezier(0.4, 0, 0.2, 1)',
                  '&:hover': {
                    borderColor: mood === 'great' ? '#4caf50' :
                                 mood === 'good' ? '#8bc34a' :
                                 mood === 'okay' ? '#ffc107' :
                                 mood === 'bad' ? '#ff9800' :
                                 mood === 'terrible' ? '#f44336' :
                                 'rgba(255, 255, 255, 0.3)',
                    backgroundColor: mood === 'great' ? 'rgba(76, 175, 80, 0.15)' :
                                     mood === 'good' ? 'rgba(139, 195, 74, 0.15)' :
                                     mood === 'okay' ? 'rgba(255, 193, 7, 0.15)' :
                                     mood === 'bad' ? 'rgba(255, 152, 0, 0.15)' :
                                     mood === 'terrible' ? 'rgba(244, 67, 54, 0.15)' :
                                     'rgba(255, 255, 255, 0.05)',
                    transform: 'scale(1.1) translateY(-4px)',
                    boxShadow: mood === 'great' ? '0 8px 24px rgba(76, 175, 80, 0.3)' :
                               mood === 'good' ? '0 8px 24px rgba(139, 195, 74, 0.3)' :
                               mood === 'okay' ? '0 8px 24px rgba(255, 193, 7, 0.3)' :
                               mood === 'bad' ? '0 8px 24px rgba(255, 152, 0, 0.3)' :
                               mood === 'terrible' ? '0 8px 24px rgba(244, 67, 54, 0.3)' :
                               '0 8px 24px rgba(255, 255, 255, 0.2)'
                  }
                }}
              >
                {mood === 'great' ? (
                  <SentimentVerySatisfiedIcon sx={{ fontSize: 32, color: '#4caf50', transition: 'all 0.3s' }} />
                ) : mood === 'good' ? (
                  <SentimentSatisfiedIcon sx={{ fontSize: 32, color: '#8bc34a', transition: 'all 0.3s' }} />
                ) : mood === 'okay' ? (
                  <SentimentNeutralIcon sx={{ fontSize: 32, color: '#ffc107', transition: 'all 0.3s' }} />
                ) : mood === 'bad' ? (
                  <SentimentDissatisfiedIcon sx={{ fontSize: 32, color: '#ff9800', transition: 'all 0.3s' }} />
                ) : mood === 'terrible' ? (
                  <SentimentVeryDissatisfiedIcon sx={{ fontSize: 32, color: '#f44336', transition: 'all 0.3s' }} />
                ) : (
                  <SentimentNeutralIcon sx={{ fontSize: 32, color: 'rgba(255, 255, 255, 0.4)', transition: 'all 0.3s' }} />
                )}
                {mood && (
                  <Fade in={!!mood}>
                    <CheckCircleIcon
                      sx={{
                        fontSize: 20,
                        color: mood === 'great' ? '#4caf50' :
                               mood === 'good' ? '#8bc34a' :
                               mood === 'okay' ? '#ffc107' :
                               mood === 'bad' ? '#ff9800' :
                               mood === 'terrible' ? '#f44336' :
                               'success.main',
                        position: 'absolute',
                        top: -4,
                        right: -4,
                        backgroundColor: 'background.paper',
                        borderRadius: '50%'
                      }}
                    />
                  </Fade>
                )}
              </IconButton>
            </Tooltip>
          </Grow>
          <WellnessIcon
            icon={FitnessCenterIcon}
            checked={exercised}
            onClick={() => setExercised(!exercised)}
            title="Exercise"
            delay={100}
            color="#2196f3"
          />
          <WellnessIcon
            icon={AppleIcon}
            checked={eatenFruits}
            onClick={() => setEatenFruits(!eatenFruits)}
            title="Fruits & Vegetables"
            delay={200}
            color="#4caf50"
          />
          <WellnessIcon
            icon={BedIcon}
            checked={sleptWell}
            onClick={() => setSleptWell(!sleptWell)}
            title="Sleep"
            delay={300}
            color="#673ab7"
          />
        </Box>

        {/* Tasks Section */}
        {(overdueTodos.length > 0 || upcomingTodos.length > 0) && (
          <Fade in={mounted} timeout={1000}>
            <Box sx={{ mb: 6 }}>
              {/* Overdue Tasks */}
              {overdueTodos.length > 0 && (
                <Box sx={{ mb: 4 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 2.5 }}>
                    <AssignmentLateIcon sx={{ color: '#ef5350', fontSize: 20, opacity: 0.9 }} />
                    <Typography
                      variant="subtitle2"
                      sx={{
                        color: '#ef5350',
                        fontWeight: 600,
                        textTransform: 'uppercase',
                        letterSpacing: '0.1em',
                        fontSize: '0.75rem'
                      }}
                    >
                      Overdue
                    </Typography>
                    <Chip
                      label={overdueTodos.length}
                      size="small"
                      sx={{
                        height: 20,
                        fontSize: '0.7rem',
                        fontWeight: 600,
                        backgroundColor: 'rgba(239, 83, 80, 0.2)',
                        color: '#ef5350',
                        border: '1px solid rgba(239, 83, 80, 0.3)'
                      }}
                    />
                  </Box>
                  {(showAllOverdue ? overdueTodos : overdueTodos.slice(0, 3)).map((todo, index) => (
                    <Grow in={mounted} timeout={600 + index * 100} key={todo.id}>
                      <Box
                        onClick={() => onSelectList(todo.listId)}
                        sx={{
                          display: 'flex',
                          alignItems: 'center',
                          gap: 2,
                          py: 1.75,
                          px: 2.5,
                          mb: 1,
                          borderRadius: 2,
                          cursor: 'pointer',
                          backgroundColor: 'rgba(239, 83, 80, 0.12)',
                          border: '1px solid rgba(239, 83, 80, 0.25)',
                          backdropFilter: 'blur(8px)',
                          transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                          '&:hover': {
                            backgroundColor: 'rgba(239, 83, 80, 0.18)',
                            borderColor: 'rgba(239, 83, 80, 0.4)',
                            transform: 'translateX(8px)'
                          }
                        }}
                      >
                        <IconButton
                          size="small"
                          onClick={(e) => handleToggleTodoComplete(todo, e)}
                          sx={{
                            p: 0,
                            '&:hover': {
                              backgroundColor: 'transparent'
                            }
                          }}
                        >
                          <RadioButtonUncheckedIcon sx={{ fontSize: 18, color: '#ef5350', opacity: 0.7 }} />
                        </IconButton>
                        <Typography
                          variant="body2"
                          sx={{
                            flexGrow: 1,
                            color: 'rgba(255, 255, 255, 0.85)',
                            fontWeight: 400
                          }}
                        >
                          {todo.text}
                          <Typography
                            component="span"
                            variant="caption"
                            sx={{
                              ml: 1.5,
                              color: 'rgba(255, 255, 255, 0.4)',
                              fontSize: '0.65rem',
                              fontStyle: 'italic'
                            }}
                          >
                            {todo.listName}
                          </Typography>
                        </Typography>
                        {todo.dueDate && (
                          <Typography
                            variant="caption"
                            sx={{
                              color: '#ef5350',
                              opacity: 0.8,
                              fontSize: '0.7rem',
                              fontWeight: 500
                            }}
                          >
                            {formatDate(todo.dueDate)}
                          </Typography>
                        )}
                      </Box>
                    </Grow>
                  ))}
                  {overdueTodos.length > 3 && (
                    <Typography
                      variant="caption"
                      onClick={() => setShowAllOverdue(!showAllOverdue)}
                      sx={{
                        color: 'rgba(255, 255, 255, 0.4)',
                        pl: 2,
                        fontSize: '0.7rem',
                        cursor: 'pointer',
                        '&:hover': {
                          color: 'rgba(255, 255, 255, 0.6)'
                        }
                      }}
                    >
                      {showAllOverdue ? 'Show less' : `+${overdueTodos.length - 3} more`}
                    </Typography>
                  )}
                </Box>
              )}

              {/* Upcoming Tasks and Birthdays */}
              {(upcomingTodos.length > 0 || upcomingBirthdays.length > 0) && (
                <Box>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 2.5 }}>
                    <WatchLaterIcon sx={{ color: '#ffa726', fontSize: 20, opacity: 0.9 }} />
                    <Typography
                      variant="subtitle2"
                      sx={{
                        color: '#ffa726',
                        fontWeight: 600,
                        textTransform: 'uppercase',
                        letterSpacing: '0.1em',
                        fontSize: '0.75rem'
                      }}
                    >
                      Upcoming
                    </Typography>
                    <Chip
                      label={upcomingTodos.length + upcomingBirthdays.length}
                      size="small"
                      sx={{
                        height: 20,
                        fontSize: '0.7rem',
                        fontWeight: 600,
                        backgroundColor: 'rgba(255, 167, 38, 0.2)',
                        color: '#ffa726',
                        border: '1px solid rgba(255, 167, 38, 0.3)'
                      }}
                    />
                  </Box>
                  {/* Combine todos and birthdays, sort by date */}
                  {(() => {
                    const combined = [
                      ...upcomingTodos.map(item => ({ ...item, type: 'todo', sortDate: item.dueDate })),
                      ...upcomingBirthdays.map(item => ({ ...item, type: 'birthday', sortDate: item.nextBirthday }))
                    ].sort((a, b) => parseISO(a.sortDate) - parseISO(b.sortDate));

                    const displayItems = showAllUpcoming ? combined : combined.slice(0, 3);

                    return (
                      <>
                        {displayItems.map((item, index) => (
                          <Grow in={mounted} timeout={700 + index * 100} key={`${item.type}-${item.id}`}>
                            <Box
                              onClick={() => onSelectList(item.listId)}
                              sx={{
                                display: 'flex',
                                alignItems: 'center',
                                gap: 2,
                                py: 1.75,
                                px: 2.5,
                                mb: 1,
                                borderRadius: 2,
                                cursor: 'pointer',
                                backgroundColor: 'rgba(255, 167, 38, 0.12)',
                                border: '1px solid rgba(255, 167, 38, 0.25)',
                                backdropFilter: 'blur(8px)',
                                transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                                '&:hover': {
                                  backgroundColor: 'rgba(255, 167, 38, 0.18)',
                                  borderColor: 'rgba(255, 167, 38, 0.4)',
                                  transform: 'translateX(8px)'
                                }
                              }}
                            >
                              {item.type === 'birthday' ? (
                                <CakeIcon sx={{ fontSize: 18, color: '#ffa726', opacity: 0.7 }} />
                              ) : (
                                <IconButton
                                  size="small"
                                  onClick={(e) => handleToggleTodoComplete(item, e)}
                                  sx={{
                                    p: 0,
                                    '&:hover': {
                                      backgroundColor: 'transparent'
                                    }
                                  }}
                                >
                                  <RadioButtonUncheckedIcon sx={{ fontSize: 18, color: '#ffa726', opacity: 0.7 }} />
                                </IconButton>
                              )}
                              <Typography
                                variant="body2"
                                sx={{
                                  flexGrow: 1,
                                  color: 'rgba(255, 255, 255, 0.85)',
                                  fontWeight: 400
                                }}
                              >
                                {item.type === 'birthday' ? `${item.name}'s Birthday` : item.text}
                                <Typography
                                  component="span"
                                  variant="caption"
                                  sx={{
                                    ml: 1.5,
                                    color: 'rgba(255, 255, 255, 0.4)',
                                    fontSize: '0.65rem',
                                    fontStyle: 'italic'
                                  }}
                                >
                                  {item.listName}
                                </Typography>
                              </Typography>
                              <Typography
                                variant="caption"
                                sx={{
                                  color: '#ffa726',
                                  opacity: 0.8,
                                  fontSize: '0.7rem',
                                  fontWeight: 500
                                }}
                              >
                                {item.type === 'birthday'
                                  ? item.daysUntil === 0
                                    ? 'Today'
                                    : item.daysUntil === 1
                                    ? 'Tomorrow'
                                    : `${item.daysUntil}d`
                                  : formatDate(item.dueDate)}
                              </Typography>
                            </Box>
                          </Grow>
                        ))}
                        {combined.length > 3 && (
                          <Typography
                            variant="caption"
                            onClick={() => setShowAllUpcoming(!showAllUpcoming)}
                            sx={{
                              color: 'rgba(255, 255, 255, 0.4)',
                              pl: 2,
                              fontSize: '0.7rem',
                              cursor: 'pointer',
                              '&:hover': {
                                color: 'rgba(255, 255, 255, 0.6)'
                              }
                            }}
                          >
                            {showAllUpcoming ? 'Show less' : `+${combined.length - 3} more`}
                          </Typography>
                        )}
                      </>
                    );
                  })()}
                </Box>
              )}
            </Box>
          </Fade>
        )}
      </Box>

      {/* Mood Selector Popover */}
      <Popover
        open={Boolean(moodAnchorEl)}
        anchorEl={moodAnchorEl}
        onClose={() => setMoodAnchorEl(null)}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'center',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'center',
        }}
        PaperProps={{
          sx: {
            mt: 1,
            backgroundColor: 'rgba(30, 30, 30, 0.95)',
            backdropFilter: 'blur(10px)',
            borderRadius: 3,
            p: 2,
            boxShadow: '0 8px 32px rgba(0, 0, 0, 0.4)'
          }
        }}
      >
        <Box sx={{ display: 'flex', gap: 1.5 }}>
          {[
            { value: 'great', icon: SentimentVerySatisfiedIcon, label: 'Great', color: '#4caf50' },
            { value: 'good', icon: SentimentSatisfiedIcon, label: 'Good', color: '#8bc34a' },
            { value: 'okay', icon: SentimentNeutralIcon, label: 'Okay', color: '#ffc107' },
            { value: 'bad', icon: SentimentDissatisfiedIcon, label: 'Bad', color: '#ff9800' },
            { value: 'terrible', icon: SentimentVeryDissatisfiedIcon, label: 'Terrible', color: '#f44336' }
          ].map(({ value, icon: Icon, label, color }) => (
            <IconButton
              key={value}
              aria-label={label}
              title={label}
              onClick={() => {
                setMood(value);
                setMoodAnchorEl(null);
              }}
              sx={{
                position: 'relative',
                p: 0,
                width: 55,
                height: 55,
                borderRadius: '50%',
                border: '2px solid',
                borderColor: mood === value ? color : 'rgba(255, 255, 255, 0.1)',
                backgroundColor: mood === value ? `${color}20` : 'transparent',
                transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                '&:hover': {
                  borderColor: color,
                  backgroundColor: `${color}15`,
                  transform: 'scale(1.1)',
                  boxShadow: `0 6px 20px ${color}40`
                }
              }}
            >
              <Icon
                sx={{
                  fontSize: 26,
                  color: mood === value ? color : 'rgba(255, 255, 255, 0.6)',
                  transition: 'all 0.3s'
                }}
              />
            </IconButton>
          ))}
        </Box>
      </Popover>
    </Box>
  );
};

export default Dashboard;
